import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { leadsAPI, usersAPI, organizationsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { PlusIcon, MagnifyingGlassIcon, UserIcon, PhoneIcon, EnvelopeIcon, BuildingOfficeIcon, ArchiveBoxIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import Modal from '../../components/UI/Modal';
import { KanbanSkeleton } from '../../components/UI/Skeleton';
import UserAvatar from '../../components/UI/UserAvatar';
import { LeadDetailContent } from './LeadDetail';

const ACTIVE_LEAD_STATUSES = [
  { id: 'new', name: 'New', color: 'bg-blue-500' },
  { id: 'contacted', name: 'Contacted', color: 'bg-yellow-500' },
  { id: 'qualified', name: 'Qualified', color: 'bg-green-500' },
  { id: 'proposal', name: 'Proposal Sent', color: 'bg-purple-500' },
  { id: 'negotiation', name: 'Negotiation', color: 'bg-orange-500' },
];

const ARCHIVED_LEAD_STATUSES = [
  { id: 'converted', name: 'Converted', color: 'bg-emerald-500' },
  { id: 'lost', name: 'Lost', color: 'bg-red-500' },
];

const ALL_LEAD_STATUSES = [...ACTIVE_LEAD_STATUSES, ...ARCHIVED_LEAD_STATUSES];

const Leads = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const queryClient = useQueryClient();

  // Get current user info from token
  const currentUser = useMemo(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload;
      } catch (e) {
        return null;
      }
    }
    return null;
  }, []);

  const isAdminOrManager = currentUser && (currentUser.role === 'admin' || currentUser.role === 'manager');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    jobTitle: '',
    website: '',
    source: '',
    status: 'new',
    rating: 'warm',
    assignedTo: '',
    customerOrganization: '',
    address: {
      street: '',
      city: '',
      state: '',
      country: '',
      zipCode: '',
    },
  });

  const { data: leads, isLoading } = useQuery({
    queryKey: ['leads', searchTerm],
    queryFn: async () => {
      const params = { limit: 1000, ...(searchTerm ? { search: searchTerm } : {}) };
      const response = await leadsAPI.getAll(params);
      return response.data.data;
    },
  });

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await usersAPI.getAll();
      return response.data.data;
    },
  });

  const { data: organizations } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const response = await organizationsAPI.getAll();
      return response.data.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: leadsAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['leads']);
      toast.success('Lead created successfully');
      closeModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create lead');
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }) => leadsAPI.update(id, { status }),
    onError: (error) => {
      // Rollback: refetch to restore correct state
      queryClient.invalidateQueries(['leads']);
      toast.error(error.response?.data?.message || 'Failed to update lead status');
    },
  });

  const handleDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    // Dropped outside the list
    if (!destination) return;

    // Dropped in the same position
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    const newStatus = destination.droppableId;

    // Optimistic update: move the card instantly in the cache
    queryClient.setQueryData(['leads', searchTerm], (old) => {
      if (!old) return old;

      // Build grouped columns from current data
      const columns = {};
      ACTIVE_LEAD_STATUSES.forEach(s => { columns[s.id] = []; });
      old.forEach(lead => {
        if (columns[lead.status]) columns[lead.status].push(lead);
      });

      // Remove the dragged item from its source column
      const [movedItem] = columns[source.droppableId].splice(source.index, 1);
      if (!movedItem) return old;

      // Update the status on the moved item
      const updatedItem = { ...movedItem, status: newStatus };

      // Insert at the destination index
      columns[destination.droppableId].splice(destination.index, 0, updatedItem);

      // Flatten back to a single array (keep archived leads too)
      const activeFlatted = ACTIVE_LEAD_STATUSES.flatMap(s => columns[s.id]);
      const archivedLeads = old.filter(l => ARCHIVED_LEAD_STATUSES.some(a => a.id === l.status));
      return [...activeFlatted, ...archivedLeads];
    });

    // Fire the API call
    updateStatusMutation.mutate({ id: draggableId, status: newStatus });
  };

  const handleCardClick = (e, lead) => {
    e.preventDefault();
    setSelectedLead(lead);
    setIsDetailModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      jobTitle: '',
      website: '',
      source: '',
      status: 'new',
      rating: 'warm',
      assignedTo: '',
      customerOrganization: '',
      address: {
        street: '',
        city: '',
        state: '',
        country: '',
        zipCode: '',
      },
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Clean up the payload - remove empty address fields
    const payload = { ...formData };
    
    // Only include address if at least one field has a value
    const hasAddress = Object.values(formData.address).some(val => val && val.trim() !== '');
    if (!hasAddress) {
      delete payload.address;
    } else {
      // Remove empty address fields
      payload.address = Object.fromEntries(
        Object.entries(formData.address).filter(([_, v]) => v && v.trim() !== '')
      );
    }
    
    // Remove empty optional fields
    if (!payload.source) delete payload.source;
    if (!payload.phone) delete payload.phone;
    if (!payload.company) delete payload.company;
    if (!payload.jobTitle) delete payload.jobTitle;
    if (!payload.website) delete payload.website;
    if (!payload.assignedTo) delete payload.assignedTo;
    
    createMutation.mutate(payload);
  };

  const groupedLeads = ACTIVE_LEAD_STATUSES.reduce((acc, status) => {
    acc[status.id] = leads?.filter((lead) => lead.status === status.id) || [];
    return acc;
  }, {});

  const archivedLeads = leads?.filter(lead => ARCHIVED_LEAD_STATUSES.some(s => s.id === lead.status)) || [];

  if (isLoading) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Leads</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Manage and track your sales leads</p>
          </div>
          <button className="btn btn-primary">
            <PlusIcon className="w-4 h-4 mr-1.5" />
            Add Lead
          </button>
        </div>
        <div className="bg-white dark:bg-surface-800 shadow-sm rounded-xl border border-gray-200 dark:border-surface-700 p-3">
          <div className="h-9 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse"></div>
        </div>
        <KanbanSkeleton columns={5} cardsPerColumn={3} />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-5"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Leads</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Manage and track your sales leads</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.96 }}
          onClick={() => setIsModalOpen(true)} 
          className="btn btn-primary"
        >
          <PlusIcon className="w-4 h-4 mr-1.5" />
          Add Lead
        </motion.button>
      </div>

      {/* Search and Tab Toggle */}
      <div className="card-premium rounded-2xl p-3">
        <div className="flex items-center space-x-3">
          <div className="flex-1 relative search-glow">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search leads by name, email, company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 dark:bg-surface-700 border-0 rounded-lg focus:bg-white dark:focus:bg-surface-600 focus:ring-2 focus:ring-primary-500/20 transition-all placeholder-gray-400 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
            <button
              onClick={() => setActiveTab('active')}
              className={`flex items-center px-3 py-2 text-xs font-medium transition-colors ${
                activeTab === 'active'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-surface-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-surface-600'
              }`}
            >
              <FunnelIcon className="w-4 h-4 mr-1" />
              Pipeline
            </button>
            <button
              onClick={() => setActiveTab('archived')}
              className={`flex items-center px-3 py-2 text-xs font-medium transition-colors ${
                activeTab === 'archived'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-surface-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-surface-600'
              }`}
            >
              <ArchiveBoxIcon className="w-4 h-4 mr-1" />
              Archived
              {archivedLeads.length > 0 && (
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === 'archived' ? 'bg-white/20 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                }`}>
                  {archivedLeads.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Active Pipeline - Kanban Board */}
      {activeTab === 'active' && (
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {ACTIVE_LEAD_STATUSES.map((status, colIdx) => (
            <Droppable key={status.id} droppableId={status.id}>
              {(provided, snapshot) => (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: colIdx * 0.06, type: 'spring', stiffness: 300, damping: 25 }}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`rounded-2xl overflow-hidden transition-all duration-200 bg-gray-50 dark:bg-surface-800 border border-gray-200 dark:border-surface-700 ${
                    snapshot.isDraggingOver ? 'ring-2 ring-primary-400/50 border-primary-300 scale-[1.01]' : ''
                  }`}
                >
                  {/* Column Header */}
                  <div className="px-3 py-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className={`w-2.5 h-2.5 rounded-full ${status.color} shadow-sm`} />
                        <h3 className="font-semibold text-xs uppercase tracking-wider text-gray-700 dark:text-gray-300">{status.name}</h3>
                      </div>
                      <span className="bg-gray-200/80 dark:bg-gray-600/50 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full text-xs font-medium">
                        {groupedLeads[status.id].length}
                      </span>
                    </div>
                  </div>

                  {/* Lead Cards */}
                  <div className="p-2 space-y-2 min-h-[400px]">
                    {groupedLeads[status.id].map((lead, index) => (
                      <Draggable key={lead._id} draggableId={lead._id} index={index}>
                        {(provided, snapshot) => {
                          const card = (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            onClick={(e) => handleCardClick(e, lead)}
                            className={`kanban-drag-card rounded-lg p-3 cursor-pointer group transition-all duration-200 ${
                              snapshot.isDragging 
                                ? 'is-dragging shadow-2xl rotate-2 scale-105' 
                                : ''
                            }`}
                            style={snapshot.isDragging ? { ...provided.draggableProps.style, zIndex: 99999 } : provided.draggableProps.style}
                          >
                            {/* Rating Badge */}
                            <div className="flex items-center justify-between mb-3">
                              <span
                                className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                  lead.rating === 'hot' 
                                    ? 'text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-900/20'
                                    : lead.rating === 'warm'
                                    ? 'text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-900/20'
                                    : 'text-blue-700 bg-blue-50 dark:text-blue-300 dark:bg-blue-900/20'
                                }`}
                              >
                                {lead.rating?.toUpperCase()}
                              </span>
                            </div>

                            {/* Lead Name */}
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-1.5 text-sm group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                              {lead.firstName} {lead.lastName}
                            </h4>

                            {/* Organization */}
                            {(lead.customerOrganization?.name || lead.organization?.name) && (
                              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-2">
                                <BuildingOfficeIcon className="w-4 h-4 mr-2 flex-shrink-0 text-gray-400 group-hover:text-primary-400 transition-colors" />
                                <span className="truncate">{lead.customerOrganization?.name || lead.organization?.name}</span>
                              </div>
                            )}

                            {/* Email */}
                            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-2">
                              <EnvelopeIcon className="w-4 h-4 mr-2 flex-shrink-0 text-gray-400 group-hover:text-primary-400 transition-colors" />
                              <span className="truncate">{lead.email}</span>
                            </div>

                            {/* Phone */}
                            {lead.phone && (
                              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-2">
                                <PhoneIcon className="w-4 h-4 mr-2 flex-shrink-0 text-gray-400 group-hover:text-primary-400 transition-colors" />
                                <span>{lead.phone}</span>
                              </div>
                            )}

                            {/* Assignee */}
                            <div className="flex items-center mt-2 pt-2 border-t border-gray-100 dark:border-gray-700/40">
                              {lead.assignedTo ? (
                                <>
                                  <UserAvatar user={lead.assignedTo} size="xs" />
                                  <span className="ml-2 text-xs text-gray-600 dark:text-gray-400">{lead.assignedTo.firstName} {lead.assignedTo.lastName}</span>
                                </>
                              ) : (
                                <>
                                  <div className="w-6 h-6 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-gray-500 dark:text-gray-400">
                                    <UserIcon className="w-3 h-3" />
                                  </div>
                                  <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">Unassigned</span>
                                </>
                              )}
                            </div>
                          </div>
                          );
                          return snapshot.isDragging ? ReactDOM.createPortal(card, document.body) : card;
                        }}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                </motion.div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>
      )}

      {/* Archived Leads - Table View */}
      {activeTab === 'archived' && (
        <div className="bg-white dark:bg-surface-800 shadow-sm rounded-xl border border-gray-200 dark:border-surface-700 overflow-hidden">
          {archivedLeads.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead>
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Lead</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Organization</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Email</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Rating</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Owner</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {archivedLeads.map((lead) => {
                    const statusObj = ARCHIVED_LEAD_STATUSES.find(s => s.id === lead.status);
                    return (
                      <tr
                        key={lead._id}
                        onClick={(e) => handleCardClick(e, lead)}
                        className="hover:bg-gray-50 dark:hover:bg-surface-700 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            {lead.assignedTo ? (
                              <UserAvatar user={lead.assignedTo} size="xs" />
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                                <UserIcon className="w-3 h-3 text-gray-500" />
                              </div>
                            )}
                            <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                              {lead.firstName} {lead.lastName}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {(lead.customerOrganization?.name || lead.organization?.name) ? (
                            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                              <BuildingOfficeIcon className="w-4 h-4 mr-1.5 flex-shrink-0 text-gray-400" />
                              <span className="truncate">{lead.customerOrganization?.name || lead.organization?.name}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{lead.email}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            lead.status === 'converted'
                              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'
                              : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                          }`}>
                            {statusObj?.name || lead.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                            lead.rating === 'hot'
                              ? 'text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-900/20'
                              : lead.rating === 'warm'
                              ? 'text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-900/20'
                              : 'text-blue-700 bg-blue-50 dark:text-blue-300 dark:bg-blue-900/20'
                          }`}>
                            {lead.rating?.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {lead.assignedTo ? (
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {lead.assignedTo.firstName} {lead.assignedTo.lastName}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400">Unassigned</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {lead.updatedAt ? new Date(lead.updatedAt).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-16">
              <ArchiveBoxIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">No archived leads</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Converted and lost leads will appear here.</p>
            </div>
          )}
        </div>
      )}

      {/* Create Lead Modal */}
      <Modal isOpen={isModalOpen} onClose={closeModal} title="Create New Lead" size="lg">
        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">First Name *</label>
              <input
                type="text"
                required
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="glass-input"
                placeholder="Enter first name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Last Name *</label>
              <input
                type="text"
                required
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="glass-input"
                placeholder="Enter last name"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email *</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="glass-input"
              placeholder="Enter email address"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Customer Organization *</label>
            <select
              required
              value={formData.customerOrganization}
              onChange={(e) => setFormData({ ...formData, customerOrganization: e.target.value })}
              className="glass-input"
            >
              <option value="">Select organization</option>
              {organizations?.map((org) => (
                <option key={org._id} value={org._id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="glass-input"
              placeholder="Enter phone number"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Job Title</label>
              <input
                type="text"
                value={formData.jobTitle}
                onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                className="glass-input"
                placeholder="Enter job title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Website</label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="glass-input"
                placeholder="https://example.com"
              />
            </div>
          </div>

          {isAdminOrManager ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Assigned To</label>
              <select
                value={formData.assignedTo}
                onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                className="glass-input"
              >
                <option value="">Unassigned</option>
                {users?.map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.firstName} {user.lastName}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800 p-3">
              <p className="text-sm text-primary-700 dark:text-primary-300">
                ℹ️ This lead will be automatically assigned to you
              </p>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Source</label>
              <select
                value={formData.source}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                className="glass-input"
              >
                <option value="">Select source</option>
                <option value="website">Website</option>
                <option value="referral">Referral</option>
                <option value="social_media">Social Media</option>
                <option value="email_campaign">Email Campaign</option>
                <option value="cold_call">Cold Call</option>
                <option value="trade_show">Trade Show</option>
                <option value="partner">Partner</option>
                <option value="discovery">LeadForge Discovery</option>
                <option value="linkedin">LinkedIn</option>
                <option value="apollo">Apollo</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="glass-input"
              >
                {ALL_LEAD_STATUSES.map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Rating</label>
              <select
                value={formData.rating}
                onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                className="glass-input"
              >
                <option value="hot">Hot</option>
                <option value="warm">Warm</option>
                <option value="cold">Cold</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100 dark:border-gray-700">
            <button type="button" onClick={closeModal} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={createMutation.isPending} className="btn btn-primary">
              {createMutation.isPending ? 'Creating...' : 'Create Lead'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Lead Detail Modal */}
      <Modal 
        isOpen={isDetailModalOpen} 
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedLead(null);
        }} 
        title={selectedLead ? `${selectedLead.firstName} ${selectedLead.lastName}` : 'Lead Details'}
        size="xl"
      >
        {selectedLead && (
          <div className="mt-2 max-h-[75vh] overflow-y-auto -mx-6 px-6">
            <LeadDetailContent
              leadId={selectedLead._id}
              isModal
              onClose={() => {
                setIsDetailModalOpen(false);
                setSelectedLead(null);
              }}
            />
          </div>
        )}
      </Modal>
    </motion.div>
  );
};

export default Leads;


